import { useEffect } from "react";
import * as Sentry from "@sentry/react";

/**
 * مدیریت global error handlers با Sentry
 * - Cleanup خودکار هنگام unmount
 * - فقط وقتی Sentry initialize شده باشد
 * - فقط در production
 */
function SentryErrorHandler() {
  useEffect(() => {
    const sentryEnabled =
      process.env.NODE_ENV === "production" ||
      process.env.REACT_APP_SENTRY_ENV === "test";
    if (typeof window === "undefined" || !sentryEnabled) return;

    const isSentryReady = () => {
      try {
        return Sentry.getClient() !== undefined;
      } catch {
        return false;
      }
    };

    let timeoutId;
    let cleanupFn;

    const setupErrorHandlers = () => {
      if (!isSentryReady()) {
        timeoutId = setTimeout(setupErrorHandlers, 50);
        return;
      }

      const handleError = (event) => {
        if (event.error) {
          Sentry.captureException(event.error);
        } else if (event.message) {
          Sentry.captureException(new Error(event.message));
        }
      };

      const handleUnhandledRejection = (event) => {
        Sentry.captureException(
          event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason))
        );
      };

      window.addEventListener("error", handleError);
      window.addEventListener("unhandledrejection", handleUnhandledRejection);

      cleanupFn = () => {
        window.removeEventListener("error", handleError);
        window.removeEventListener("unhandledrejection", handleUnhandledRejection);
        if (timeoutId) clearTimeout(timeoutId);
      };
    };

    setupErrorHandlers();

    return () => {
      if (typeof cleanupFn === "function") cleanupFn();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return null;
}

export default SentryErrorHandler;
